import { useEffect, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  useLazyGetExpenseItemsByExpenseItemIdQuery,
  useUserUnMergeLineItemsMutation,
} from '../../../services/ExpenseApi'
import { KTSVG } from '../../../_metronic/helpers'
import MergeSelectedLineItemHeader, {
  formatReceiptNumberDisplay,
  getReceiptNumber,
} from './MergeSelectedLineItemHeader'

type Props = {
  show: boolean
  onClose: () => void
  selectedDraftData?: any
  refetch?: (expenseId: string) => void
  onSuccess?: () => void
}

export default function UnMergeConfirmationModal({
  show,
  onClose,
  refetch,
  selectedDraftData,
  onSuccess,
}: Props) {
  const [unMergeLineItem, { isLoading }] = useUserUnMergeLineItemsMutation()
  const [fetchItem] = useLazyGetExpenseItemsByExpenseItemIdQuery()
  const [currentItem, setCurrentItem] = useState<any>(null)
  const [mergedFromItem, setMergedFromItem] = useState<any>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)

  const currentId = Number(selectedDraftData?.expenseItemId || 0)
  const mergedFromId = Number(
    selectedDraftData?.mergeExpenseitemId || currentItem?.mergeExpenseitemId || 0
  )

  const keptItem = currentItem || selectedDraftData
  const keptReceiptDisplay = formatReceiptNumberDisplay(getReceiptNumber(keptItem))
  const fromReceiptDisplay = formatReceiptNumberDisplay(getReceiptNumber(mergedFromItem))

  useEffect(() => {
    if (!show || !currentId) {
      setCurrentItem(null)
      setMergedFromItem(null)
      return
    }

    let cancelled = false
    ;(async () => {
      setLoadingDetails(true)
      try {
        const currentRes: any = await fetchItem({ expenseItemId: currentId }).unwrap()
        const current = currentRes?.result || null
        if (!cancelled) setCurrentItem(current)

        const fromId = Number(
          selectedDraftData?.mergeExpenseitemId || current?.mergeExpenseitemId || 0
        )
        if (fromId > 0) {
          try {
            const fromRes: any = await fetchItem({
              expenseItemId: fromId,
              includeDeleted: true,
            }).unwrap()
            if (!cancelled) setMergedFromItem(fromRes?.result || null)
          } catch {
            if (!cancelled) setMergedFromItem(null)
          }
        } else if (!cancelled) {
          setMergedFromItem(null)
        }
      } catch {
        if (!cancelled) {
          setCurrentItem(null)
          setMergedFromItem(null)
        }
      } finally {
        if (!cancelled) setLoadingDetails(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [show, currentId, selectedDraftData?.mergeExpenseitemId, fetchItem])

  const handleConfirmClick = async () => {
    if (!currentId) return
    try {
      await unMergeLineItem({ ccExpenseItemID: currentId }).unwrap()
      refetch?.(selectedDraftData?.expenseId)
      onSuccess?.()
      toast.success('Line item unmerged successfully')
      onClose()
    } catch {
      toast.error('Failed to unmerge')
    }
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      fullscreen
      dialogClassName="unmerge-confirm-dialog modal-dialog modal-fullscreen ss-modal-100"
      contentClassName="unmerge-confirm-content"
    >
      <Modal.Header closeButton className="d-flex align-items-center justify-content-between">
        <Modal.Title className="fs-4 fw-bolder">UnMerge LineItem</Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        <p className="mb-2">
          Are you sure you want to UnMerge these line items? After UnMerge, both expense items below
          will be available again.
        </p>
        <div className="alert alert-info py-2 fs-7 mb-3">
          <strong>What happens:</strong> the current (kept) line item stays active, and the
          UnMerge line item is restored as its own expense item again.
        </div>

        {loadingDetails ? (
          <div className="text-center py-8">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <div className="d-flex flex-nowrap align-items-stretch mb-4" style={{ gap: 16 }}>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <MergeSelectedLineItemHeader
                  selectedDraftData={keptItem}
                  title={`Kept Line Item — Receipt # ${keptReceiptDisplay}${
                    currentId ? ` · SS Item #${currentId}` : ''
                  }`}
                  variant="into"
                />
              </div>
              <div
                className="d-flex flex-column align-items-center justify-content-center px-2"
                style={{ flex: '0 0 auto', width: 56 }}
              >
                <KTSVG path="/media/icons/duotune/general/unmerge.svg" className="svg-icon-2hx mb-1 text-primary" />
                <span className="text-muted text-center fw-semibold" style={{ fontSize: 11 }}>
                  UnMerge
                </span>
              </div>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                {mergedFromItem || mergedFromId > 0 ? (
                  <MergeSelectedLineItemHeader
                    selectedDraftData={
                      mergedFromItem || {
                        expenseItemId: mergedFromId,
                        mergeExpenseitemId: mergedFromId,
                      }
                    }
                    title={`UnMerge Line Item — Receipt # ${fromReceiptDisplay}${
                      mergedFromId ? ` · SS Item #${mergedFromId}` : ''
                    }`}
                    variant="from"
                  />
                ) : (
                  <div className="border rounded p-4 h-100 bg-light">
                    <div className="badge badge-light-warning mb-2">UnMerge Line Item</div>
                    <div className="text-muted fs-7">Merged-from line item details not available.</div>
                  </div>
                )}
              </div>
            </div>

            <div className="border rounded p-3 bg-light-success">
              <div className="badge badge-success mb-2">After UnMerge — available items</div>
              <ul className="fs-7 mb-0 ps-3">
                <li className="mb-2">
                  <strong>Item #{currentId || '—'}</strong> remains available
                  {keptItem?.invoiceDataDraftId
                    ? ` (SS Receipt ID: ${keptItem.invoiceDataDraftId})`
                    : ''}
                  .
                </li>
                <li className="mb-2">
                  <strong>Item #{mergedFromId || '—'}</strong> is restored and available again
                  {mergedFromItem?.invoiceDataDraftId
                    ? ` (SS Receipt ID: ${mergedFromItem.invoiceDataDraftId})`
                    : ''}
                  .
                </li>
                <li>You can edit, attach receipts, or merge either item separately after this.</li>
              </ul>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-end align-items-center flex-wrap gap-2">
        <button className="btn btn-sm btn-light-danger" onClick={onClose} disabled={isLoading}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-danger"
          onClick={handleConfirmClick}
          disabled={isLoading || !currentId}
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              UnMerging...
            </>
          ) : (
            'UnMerge LineItem'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
